import React, { useState } from 'react';
import { Button, ProgressCircular, Heading } from '@americanexpress/dls-react';
import { IconCancelCircle, IconWarning } from '@americanexpress/dls-icons';
import { addLogToReport } from '@americanexpress/one-app-ducks';
import { useDispatch, useSelector } from 'react-redux';
import PermissionsPage from './PermissionsPage';
import CommonAlertDialog from '../CommonAlertDialog';
import ErrorScreen from '../ErrorScreen';
import styles from '../../assets/tablepage.scss';
import { deleteConsent } from '../../ducks/deleteConsentApi';
import { getVrpDetails } from '../../ducks/getVrpDetailsApi';
import {
  DASHBOARD_ROOT,
  GATEWAY_APP_CONFIG_URL,
  STORE_CONFIG, STORE_DASHBOARD, STORE_MODULES,
  TRACKING_ID,
  X_PWB_JWT_TOKEN,
} from '../../constants/appConstants';
import { VRP_DASHBOARD_DELETE_PATH } from '../../constants/apiConstants';
import { getClientContext } from '../../utils/deviceUtils';

const normalizeDashboardBody = (response) => response?.body ?? response ?? null;

const renderPermissionsContent = ({
  isLoading,
  isApiError,
  permissionsPayload,
  handleOpenCancelModel,
  isDeletePendingInSession,
}) => {
  if (isLoading) {
    return (
      <div className={styles.center}>
        <ProgressCircular size="lg" />
      </div>
    );
  }

  if (isApiError) {
    return <ErrorScreen />;
  }

  return (
    <div>
      {permissionsPayload && (
        <PermissionsPage
          consentPayload={permissionsPayload}
          onOpenCancelDialog={handleOpenCancelModel}
          isDeletePendingInSession={isDeletePendingInSession}
        />
      )}
    </div>
  );
};

function TablePage() {
  const dispatch = useDispatch();
  const [showCancelModel, setShowCancelModel] = useState(false);
  const [showDeleteInProgressModal, setShowDeleteInProgressModal] = useState(false);
  const [showCancelFailureModal, setShowCancelFailureModal] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [isCancellingPermission, setIsCancellingPermission] = useState(false);
  const [isRefreshingDashboard, setIsRefreshingDashboard] = useState(false);
  const [isDeletePendingInSession, setIsDeletePendingInSession] = useState(false);

  const dashboardApiUrl = useSelector((state) => state.getIn([STORE_CONFIG,
    GATEWAY_APP_CONFIG_URL]));

  /* Reads the dashboard data in the root module
  state which is populated by the fetchDashboard useEffect in PwbDashboard */
  const dashboardState = useSelector((state) => state.getIn([STORE_MODULES, DASHBOARD_ROOT,
    STORE_DASHBOARD])
  );

  const dashboardPayload = normalizeDashboardBody(dashboardState?.data);
  const isLoading = dashboardState?.isLoading;
  const error = dashboardState?.error || null;
  const trackingId = useSelector((state) => state.getIn([STORE_MODULES, DASHBOARD_ROOT,
    STORE_DASHBOARD, TRACKING_ID]));
  const xPwbJwtToken = useSelector((state) => state.getIn([STORE_MODULES,
    DASHBOARD_ROOT, STORE_DASHBOARD, X_PWB_JWT_TOKEN]));
  const permissionsPayload = dashboardPayload;

  // Sets the permission to be canceled
  const handleOpenCancelModel = (permission) => {
    setSelectedPermission(permission);
    setShowCancelModel(true);
  };

  // Keep modal + selected row state in sync when user closes without confirming.
  const handleCloseCancelModel = () => {
    setShowCancelModel(false);
    setSelectedPermission(null);
  };

  const refreshDashboard = async () => {
    setIsRefreshingDashboard(true);

    const attemptRefresh = async () => {
      const result = await dispatch(getVrpDetails({
        apiUrl: dashboardApiUrl,
        trackingId,
        xPwbJwtToken,
        fetchClient: fetch,
      }));

      return {
        refreshError: result?.ok === false ? result?.message : null,
        refreshBody: result?.body || null,
        isRefreshHttpError: result?.ok === false,
      };
    };

    try {
      let {
        refreshError, refreshBody, isRefreshHttpError,
      } = await attemptRefresh();
      if (refreshError || isRefreshHttpError) {
        ({
          refreshError, refreshBody, isRefreshHttpError,
        } = await attemptRefresh());
      }

      if (refreshError || isRefreshHttpError) {
        return;
      }
      const deletePendingConsents = refreshBody?.vrp_consent_details?.DELETE_PENDING || [];
      if (deletePendingConsents.length === 0) {
        setIsDeletePendingInSession(false);
      }
    } catch {
      // Swallow refresh errors so the modal close flow always completes.
    } finally {
      setIsRefreshingDashboard(false);
    }
  };

  const handleCloseDeleteInProgressModal = async () => {
    setShowDeleteInProgressModal(false);
    setSelectedPermission(null);
    await refreshDashboard();
  };

  const handleCloseCancelFailureModal = () => {
    setShowCancelFailureModal(false);
    setSelectedPermission(null);
  };

  // Confirm cancel flow: execute delete call, validate response, then update table state.
  const handleCancelPermission = async () => {
    const resolvedConsentId = selectedPermission?.vrp_consent_id;
    if (!resolvedConsentId) {
      setShowCancelModel(false);
      setShowCancelFailureModal(true);
      return;
    }

    setShowCancelFailureModal(false);
    setIsCancellingPermission(true);

    try {
      dispatch(addLogToReport('info', 'Delete request details', {
        moduleID: 'axp-bank-permissions-dashboard-root',
        apiPath: VRP_DASHBOARD_DELETE_PATH,
        PWB_JWT_TOKEN: xPwbJwtToken,
        apiUrl: dashboardApiUrl,
        activeConsentId: resolvedConsentId,
        trackingId,
        ...getClientContext(),
      }));
      const result = await dispatch(deleteConsent({
        apiUrl: dashboardApiUrl,
        consentId: resolvedConsentId,
        trackingId,
        xPwbJwtToken,
        fetchClient: fetch,
      }));

      setShowCancelModel(false);

      if (result?.ok && result?.status === 200) {
        setIsDeletePendingInSession(true);
        setShowDeleteInProgressModal(true);
      } else if (result?.status !== 401) {
        // 401 is handled by the duck (session cleared + externalRedirect).
        // Showing the failure modal here would flash before the redirect fires.
        setShowCancelFailureModal(true);
      }
    } finally {
      setIsCancellingPermission(false);
    }
  };
  const isDashboardHttpError = dashboardState?.data?.status >= 400;
  const isDashboardApiError = dashboardPayload
    && String(dashboardPayload.status_code_type).toLowerCase() === 'error';

  const isApiError = Boolean(error || isDashboardHttpError || isDashboardApiError);
  const isPageLoading = Boolean(isLoading || isRefreshingDashboard);

  return (
    <div>
      {renderPermissionsContent({
        isLoading: isPageLoading,
        isApiError,
        permissionsPayload,
        handleOpenCancelModel,
        isDeletePendingInSession,
      })}

      <CommonAlertDialog
        icon={(
          <IconCancelCircle
            filled="true"
            title="Cancel VRP Consent icon"
            titleId="cancenl-consent-id"
            size="xl"
            color="critical"
          />
        )}
        isOpen={showCancelModel}
        title={<Heading level={2} variant="sans-xsmall-bold">Cancel Permission</Heading>}
        footer={(
          <>
            <Button variant="primary" onClick={handleCancelPermission} aria-disabled={isCancellingPermission}>
              {isCancellingPermission ? 'Cancelling...' : 'Cancel Permission'}
            </Button>
            <Button variant="secondary" onClick={handleCloseCancelModel} aria-disabled={isCancellingPermission}>Close</Button>
          </>
        )}
      >
        If you cancel this permission you can still use Pay with Bank Transfer but
        you will need to approve the payments in banking app.
      </CommonAlertDialog>

      <CommonAlertDialog
        icon={(
          <IconWarning
            filled="true"
            title="Unsuccessful VRP Consent Cancel icon"
            titleId="unsuccessful-consent-cancel-id"
            size="xl"
            color="critical"
          />
        )}
        isOpen={showCancelFailureModal}
        title={<Heading level={2} variant="sans-xsmall-bold">Cancellation of Consent Was Unsuccessful</Heading>}
        footer={(
          <Button
            onClick={handleCloseCancelFailureModal}
          >
            Close
          </Button>
        )}
      >
        <p>Cancellation of consent was unsuccessful. Please try again later</p>
      </CommonAlertDialog>

      <CommonAlertDialog
        icon={(
          <IconWarning
            filled="true"
            title="Delete in progress icon"
            titleId="delete-in-progress-id"
            size="xl"
            color="warning"
          />
          )}
        isOpen={showDeleteInProgressModal}
        title={<Heading level={2} variant="sans-xsmall-bold">Delete in progress</Heading>}
        footer={(
          <Button onClick={handleCloseDeleteInProgressModal}>
            Close
          </Button>
          )}
      >
        <p>Your delete request was accepted and is currently being processed.</p>
      </CommonAlertDialog>
    </div>
  );
}

export default TablePage;
